import { CloseButton } from "@components/buttons/close";
import {
  SHAutocomplete,
  SHButton,
  SHCheckbox,
  SHDialog,
  SHDotGroup,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { ModelScreenerFilterDTO } from "@models/model-screener/entities/modelScreenerFilter";
import { DialogContent, DialogTitle, useTheme } from "@mui/material";
import { hexToRGBA } from "@utils";
import { useToggle } from "react-use";
import { useFormContext } from "react-hook-form";
import { isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";
import { OverwriteFilterDialog } from "../overwrite-modal";
import { MSFilterItemLabel } from "../../filter-item/label";
import { MSFilterSaveModalHelpTextConstants } from "../../constants";

type MSFilterSaveModalProps = {
  open: boolean;
  onClose: () => void;
  onSaveFilter: ({
    filterName,
    isUserDefault,
    overwriteId,
  }: {
    filterName: string;
    isUserDefault: boolean;
    overwriteId?: string;
  }) => Promise<boolean>;
};
export const MSFilterSaveModal = ({
  open,
  onClose,
  onSaveFilter,
}: MSFilterSaveModalProps) => {
  const { palette } = useTheme();
  const { getValues } = useFormContext<ModelScreenerFilterDTO>();
  const modelScreenerFilterSettings = useSelector((state: RootState) => {
    return state.modelScreener.filterSettings;
  });

  const nonDefaultFilterOptions = modelScreenerFilterSettings?.filter(
    (x) => !x.isSystemDefault,
  );

  // States
  const [isChecked, setIsChecked] = useToggle(false);
  const [filterName, setFilterName] = useState<string>("");
  const [overwriteId, setOverwriteId] = useState<string | undefined>(undefined);
  const [isSaving, toggleIsSaving] = useToggle(false);
  const [displayingOptions, setDisplayingOptions] = useState<number>(0);

  // Functions
  const onModalClose = () => {
    onClose();
    setIsChecked(false);
  };

  const handleInputChange = (value: string) => {
    setFilterName(value);
  };

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleVerifySubmit = async () => {
    if (!filterName) return;
    let overwriteId = nonDefaultFilterOptions?.find(
      (item) => item?.name === filterName,
    )?.id;
    if (overwriteId) {
      setOverwriteId(overwriteId);
      return;
    }
    await handleOnSubmit();
  };

  const handleOnSubmit = async () => {
    try {
      const saveResult = await onSaveFilter({
        filterName: filterName,
        overwriteId: overwriteId,
        isUserDefault: isChecked,
      });
      if (!saveResult) return;
      onModalClose();
    } finally {
      setOverwriteId(undefined);
      toggleIsSaving(false);
    }
  };

  const isDisabled = useMemo(() => {
    return isEmpty(filterName);
  }, [filterName]);

  return (
    <form>
      <SHDialog
        open={open}
        onClose={onModalClose}
        sx={{
          "& .MuiPaper-root": {
            width: {
              xs: "40%",
              xl: "640px",
            },
            maxWidth: "50%",
            position: "relative",
          },
        }}
      >
        <DialogTitle>
          <SHStack
            sx={{
              position: "absolute",
              top: "22px",
              right: "25px",
            }}
          >
            <CloseButton width="17px" height="17px" onClick={onModalClose} />
          </SHStack>
          <SHTypography fontSize={18} fontWeight={600}>
            Save current filter
          </SHTypography>
          <SHTypography fontWeight={400} fontSize={14} disabled>
            {MSFilterSaveModalHelpTextConstants.HelpText}
          </SHTypography>
        </DialogTitle>
        <DialogContent sx={{ alignItems: "center", padding: "20px 0px" }}>
          <SHStack alignItems={"center"} gap={1}>
            <SHStack
              alignItems={"start"}
              width={"100%"}
              direction={"row"}
              gap={0.5}
            >
              <MSFilterItemLabel alignItems={"start"}>
                Filter name
              </MSFilterItemLabel>
              <SHDotGroup redDot sx={{ mt: "2px" }} />
            </SHStack>

            <SHAutocomplete
              freeSolo
              disabled={isSaving}
              value={undefined}
              options={nonDefaultFilterOptions}
              filterOptions={(options, state) => {
                const filtered = options.filter((option) =>
                  option.name
                    .toLowerCase()
                    .includes(state.inputValue.toLowerCase()),
                );

                setDisplayingOptions(filtered?.length || 0);
                return filtered;
              }}
              getOptionLabel={(option) => {
                if (typeof option === "string") return getValues().name;
                return option.name;
              }}
              onInputChange={(_, newValue) => {
                handleInputChange(newValue);
              }}
              renderOption={(props, option) => {
                return (
                  <li
                    {...props}
                    key={option.id}
                    style={{ padding: "10px 10px" }}
                  >
                    <SHStack alignItems={"center"} direction="row" spacing={1}>
                      <SHTypography variant="body3" fontWeight={500}>
                        {option.name}
                      </SHTypography>
                    </SHStack>
                  </li>
                );
              }}
              textFieldProps={{
                placeholder: "Enter filter name",
                maxLength: 100,
                onKeyDown: (event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                },
              }}
              sx={{ width: "100%" }}
              PaperComponent={displayingOptions > 0 ? undefined : () => null}
            />
            <SHStack
              direction={"row"}
              justifyContent={"center"}
              alignItems={"center"}
              gap={0.5}
            >
              <SHCheckbox
                sx={{
                  width: "20px",
                  marginRight: "5px",
                  marginTop: "-3px",
                  "&:hover": {
                    backgroundColor: "transparent !important",
                  },
                }}
                checked={isChecked}
                onClick={() => {
                  handleCheckboxChange();
                }}
              />
              <SHTypography>Save current filter as default</SHTypography>
            </SHStack>
            <SHButton
              variant="contained"
              size="extraMedium"
              sx={{
                width: "72%",
                height: "40px",
                paddingX: 3,
                "&:disabled.sh-btn-with-icon": {
                  fill: palette.text.disabled,
                  border: `1px solid ${hexToRGBA(palette.primary.dark, 0.3)}`,
                  backgroundColor: palette.primary[50],
                  color: palette.common.white,
                },
              }}
              disabled={isDisabled || isSaving}
              isLoading={isSaving}
              onClick={() => {
                toggleIsSaving(true);
                setTimeout(() => handleVerifySubmit(), 0);
              }}
            >
              Save filter
            </SHButton>
          </SHStack>
        </DialogContent>
      </SHDialog>
      {overwriteId && (
        <OverwriteFilterDialog
          onClose={() => {
            setOverwriteId(undefined);
            toggleIsSaving(false);
          }}
          onSubmit={handleOnSubmit}
        />
      )}
    </form>
  );
};
